import { Database } from "@/utils/supabase.types";
import { TempoElement } from "@/utils/tempoElement";
import { NavTreeNode } from "@/utils/types";
import { useActiveCanvas } from "@/utils/utils";
import _, { debounce } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { create } from "zustand";
import { LoadingStoryboard } from "@/components/Canvas/LoadingSpinner";
import { useSupabaseStore } from "./supabaseStore";

export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 256;
export const CREATE_NEW_IFRAME = "CREATE_NEW_IFRAME";

interface CanvasPos {
  zoomPerc?: number;
  transformX?: number;
  transformY?: number;
}

type MachineForCanvas =
  Database["public"]["Tables"]["deployment_machines"]["Row"];

export interface CanvasStore {
  // Consumers should always use useCanvasPos, which is a debounced mirror of
  // canvasIdToPosRealtime, in order to avoid adding lag to panning/zooming.
  canvasIdToPos: {
    [canvasId: string]: CanvasPos;
  };
  canvasIdToPosRealtime: {
    // Should only be used by the Canvas rendering
    [canvasId: string]: CanvasPos;
  };

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  setCanvasPosValue: (
    canvasId: string,
    values: { [key: string]: number }
  ) => void;

  setCanvasPosValueRealtime: (
    canvasId: string,
    values: { [key: string]: number }
  ) => void;

  canvasMousePos: {
    x: number;
    y: number;
  };

  iframeMousePos: {
    pageX: number;
    pageY: number;

    clientX: number;
    clientY: number;
  };

  mouseOverStoryboardId?: string;

  setCanvasMousePos: (x: number, y: number) => void;
  setIframeMousePos: (mousePos: any) => void;
  setMouseOverStoryboardId: (storyboardId: string) => void;

  elementKeyToNavNodeLookup: { [elementKey: string]: NavTreeNode };
  codebaseIdToNavNodeLookup: { [codebaseId: string]: NavTreeNode[] };
  storyboardToNavTree: { [storyboardId: string]: NavTreeNode };

  // For each storyboard, which codebase Ids were last live in it so we know when they change
  storyboardIdToCodebaseIds: { [storyboardId: string]: Set<string> };

  // For each storyboard, which codebaseId is the actual Story component that's being rendered
  storyboardIdToRootCodebaseId: { [storyboardId: string]: string };

  addElementKeysToNavNodeLookup: (elementKeyToNavNodeLookup: {
    [elementKey: string]: NavTreeNode;
  }) => void;
  addCodebaseIdToNavNodeLookup: (codebaseIdToNavNodeLookup: {
    [codebaseId: string]: NavTreeNode[];
  }) => void;
  setNavTreeForStoryboard: (storyboardId: string, navTree: NavTreeNode) => void;
  clearNavTreeForStoryboard: (storyboardId: string) => void;
  setStoryboardIdToCodebaseIds: (
    storyboardId: string,
    codebaseIds?: Set<string>
  ) => void;
  setStoryboardIdToRootCodebaseId: (
    storyboardId: string,
    rootCodebaseId: string
  ) => void;

  // HTML to set for instantly creating a storyboard
  storyboardIdToSetHTML: { [storyboardId: string]: string };
  setStoryboardIdToSetHTML: (storyboardIdToSetHTML: {
    [storyboardId: string]: string;
  }) => void;
  addStoryboardIdToSetHTML: (storyboardId: string, html: string) => void;

  menuLeft: number;
  menuTop: number;
  setMenu: (menuLeft: number, menuTop: number) => void;

  canvasComponentKey?: string;
  setCanvasComponentKey: (canvasComponentKey: string) => void;

  nodeOpenDict: { [nodeId: string]: boolean };
  setNodeOpenDict: (nodeOpenDict: { [nodeId: string]: boolean }) => void;
  onClickNodeOpen: (tempoElement: TempoElement) => void;

  resetCanvasStore: () => void;

  triggerDeleteElement: boolean;
  setTriggerDeleteElement: (triggerDeleteElement: boolean) => void;

  storyboardIdToFullHTML: { [storyboardId: string]: string };
  setFullHtmlForStoryboard: (storyboardId: string, html?: string) => void;

  storyboardIdToStatus: {
    [storyboardId: string]: {
      status: "frozen" | "live" | "loading";
      last_updated: Date;
    };
  };
  setStoryboardIdToStatus: (
    storyboardId: string,
    newStatus: {
      status: "frozen" | "live" | "loading";
      last_updated: Date;
    }
  ) => void;
  bulkSetStoryboardIdToStatus: (statusUpdates: {
    [storyboardId: string]: {
      status: "frozen" | "live" | "loading";
      last_updated: Date;
    };
  }) => void;

  usersForProject: { [projectId: string]: any[] };
  setUsersForProject: (projectId: string, users: any[]) => void;

  convexLoginUrl: string;
  setConvexLoginUrl: (convexLoginUrl: string) => void;

  expoQrCodeUrl: string;
  setExpoQrCodeUrl: (expoQrCodeUrl: string) => void;

  // Loading spinner state for paste/drag and drop
  loadingStoryboard: LoadingStoryboard | null;
  setLoadingStoryboard: (loadingStoryboard: LoadingStoryboard | null) => void;

  notifyCodebaseIdsChanged: (codebaseIds: Set<string>) => void;

  // Iframe reuse management
  iframeAssignments: { [storyboardId: string]: string }; // maps storyboard id to iframe id or "CREATE_NEW"
  setIframeAssignment: (storyboardId: string, iframeId: string) => void;
}

const setCanvasPosValueRealtime = (
  get,
  set,
  canvasId: string,
  values: { [key: string]: number }
) => {
  const currentPositions = get().canvasIdToPosRealtime;
  if (!currentPositions[canvasId]) {
    currentPositions[canvasId] = {
      zoomPerc: 1,
      transformX: 0,
      transformY: 0,
    };
  }
  Object.keys(values).forEach((key) => {
    currentPositions[canvasId][key] = values[key];
  });
  set({
    canvasIdToPosRealtime: {
      ...currentPositions,
    },
  });
};

const updateCanvasPosValueThrottled = debounce(
  (get, set, canvasId: string) => {
    const realtimeValues = get().canvasIdToPosRealtime;
    set({
      canvasIdToPos: realtimeValues,
    });
  },
  100,
  { leading: true, trailing: true }
);

export const useCanvasStore = create<CanvasStore>((set, get) => ({
  canvasIdToPos: {},
  canvasIdToPosRealtime: {},

  isLoading: false,
  setIsLoading: (isLoading: boolean) => set({ isLoading }),

  canvasMousePos: {
    x: 0,
    y: 0,
  },
  // @ts-expect-error TS2322
  iframeMousePos: null,
  // @ts-expect-error TS2322
  mouseOverStoryboardId: null,

  setCanvasMousePos: (x: number, y: number) => {
    set({ canvasMousePos: { x, y } });
  },
  setIframeMousePos: (mousePos: any) => {
    set({ iframeMousePos: mousePos });
  },
  setMouseOverStoryboardId: (storyboardId: string) => {
    set({ mouseOverStoryboardId: storyboardId });
  },

  setCanvasPosValue: (canvasId: string, values: { [key: string]: number }) => {
    let zoomPercToSet = values["zoomPerc"];

    if (zoomPercToSet !== undefined) {
      if (zoomPercToSet < MIN_ZOOM) {
        values["zoomPerc"] = MIN_ZOOM;
      } else if (zoomPercToSet > MAX_ZOOM) {
        values["zoomPerc"] = MAX_ZOOM;
      }
    }

    setCanvasPosValueRealtime(get, set, canvasId, values);
    updateCanvasPosValueThrottled(get, set, canvasId); // Follows the realtime value
  },

  elementKeyToNavNodeLookup: {},
  codebaseIdToNavNodeLookup: {},
  storyboardToNavTree: {},
  addElementKeysToNavNodeLookup: (elementKeyToNavNodeLookup: {
    [elementKey: string]: NavTreeNode;
  }) => {
    set((state) => ({
      elementKeyToNavNodeLookup: {
        ...state.elementKeyToNavNodeLookup,
        ...elementKeyToNavNodeLookup,
      },
    }));
  },
  addCodebaseIdToNavNodeLookup: (codebaseIdToNavNodeLookup: {
    [codebaseId: string]: NavTreeNode[];
  }) => {
    set((state) => ({
      codebaseIdToNavNodeLookup: {
        ...state.codebaseIdToNavNodeLookup,
        ...codebaseIdToNavNodeLookup,
      },
    }));
  },
  setNavTreeForStoryboard: (storyboardId: string, navTree: NavTreeNode) => {
    set((state) => ({
      storyboardToNavTree: {
        ...state.storyboardToNavTree,
        [storyboardId]: navTree,
      },
    }));
  },
  clearNavTreeForStoryboard: (storyboardId: string) => {
    const storyboardToNavTree = get().storyboardToNavTree;
    delete storyboardToNavTree[storyboardId];
    set({ storyboardToNavTree });
  },
  setStoryboardIdToCodebaseIds: (
    storyboardId: string,
    codebaseIds?: Set<string>
  ) => {
    if (!codebaseIds) {
      const newState = {
        ...get().storyboardIdToCodebaseIds,
      };
      delete newState[storyboardId];
      set({ storyboardIdToCodebaseIds: newState });
      return;
    }

    set((state) => ({
      storyboardIdToCodebaseIds: {
        ...state.storyboardIdToCodebaseIds,
        [storyboardId]: codebaseIds,
      },
    }));
  },

  storyboardIdToRootCodebaseId: {},
  setStoryboardIdToRootCodebaseId: (
    storyboardId: string,
    rootCodebaseId: string
  ) => {
    set((state) => ({
      storyboardIdToRootCodebaseId: {
        ...state.storyboardIdToRootCodebaseId,
        [storyboardId]: rootCodebaseId,
      },
    }));
  },

  storyboardIdToSetHTML: {},
  setStoryboardIdToSetHTML: (storyboardIdToSetHTML: {
    [storyboardId: string]: string;
  }) => {
    set({ storyboardIdToSetHTML });
  },
  addStoryboardIdToSetHTML: (storyboardId: string, html: string) => {
    set((state) => ({
      storyboardIdToSetHTML: {
        ...state.storyboardIdToSetHTML,
        [storyboardId]: html,
      },
    }));
  },

  // @ts-expect-error TS2322
  menuLeft: null,
  // @ts-expect-error TS2322
  menuTop: null,
  setMenu: (menuLeft: number, menuTop: number) => set({ menuLeft, menuTop }),

  // @ts-expect-error TS2322
  canvasComponentKey: null,
  setCanvasComponentKey: (canvasComponentKey: string) =>
    set({ canvasComponentKey }),

  nodeOpenDict: { root: true },
  setNodeOpenDict: (nodeOpenDict: { [nodeId: string]: boolean }) => {
    set({ nodeOpenDict });
  },
  onClickNodeOpen: (tempoElement: TempoElement) => {
    set((state) => ({
      nodeOpenDict: {
        ...state.nodeOpenDict,
        [tempoElement.getKey()]: !state.nodeOpenDict[tempoElement.getKey()],
      },
    }));
  },

  resetCanvasStore: () => {
    set({
      canvasIdToPos: {},
      canvasIdToPosRealtime: {},
      canvasMousePos: {
        x: 0,
        y: 0,
      },
      // @ts-expect-error TS2322
      iframeMousePos: null,
      // @ts-expect-error TS2322
      mouseOverStoryboardId: null,
      elementKeyToNavNodeLookup: {},
      codebaseIdToNavNodeLookup: {},
      storyboardToNavTree: {},
      storyboardIdToSetHTML: {},

      // @ts-expect-error TS2322
      menuLeft: null,
      // @ts-expect-error TS2322
      menuTop: null,
      // @ts-expect-error TS2322
      canvasComponentKey: null,
      nodeOpenDict: { root: true },
      storyboardIdToStatus: {},
      usersForProject: {},
      // @ts-expect-error TS2322
      convexLoginUrl: null,
      // @ts-expect-error TS2322
      expoQrCodeUrl: null,
      loadingStoryboard: null,
      storyboardIdToCodebaseIds: {},
      iframePool: {},
      iframeAssignments: {},
      availableIframes: [],
      storyboardCallbacks: {},
    });
  },

  triggerDeleteElement: false,
  setTriggerDeleteElement: (triggerDeleteElement: boolean) =>
    set({ triggerDeleteElement }),

  storyboardIdToFullHTML: {},
  setFullHtmlForStoryboard: (storyboardId: string, html?: string) => {
    if (html === undefined) {
      const newState = {
        ...get().storyboardIdToFullHTML,
      };
      delete newState[storyboardId];
      set({ storyboardIdToFullHTML: newState });
      return;
    }

    set((state) => ({
      storyboardIdToFullHTML: {
        ...state.storyboardIdToFullHTML,
        [storyboardId]: html,
      },
    }));
  },

  storyboardIdToStatus: {},
  setStoryboardIdToStatus: (
    storyboardId: string,
    newStatus: {
      status: "frozen" | "live" | "loading";
      last_updated: Date;
    }
  ) => {
    get().bulkSetStoryboardIdToStatus({
      [storyboardId]: newStatus,
    });
  },
  bulkSetStoryboardIdToStatus: (statusUpdates: {
    [storyboardId: string]: {
      status: "frozen" | "live" | "loading";
      last_updated: Date;
    };
  }) => {
    set((state) => {
      return {
        storyboardIdToStatus: {
          ...state.storyboardIdToStatus,
          ...statusUpdates,
        },
      };
    });
  },

  usersForProject: {},
  setUsersForProject: (projectId: string, users: any[]) => {
    set((state) => ({
      usersForProject: {
        ...state.usersForProject,
        [projectId]: users,
      },
    }));
  },

  // @ts-expect-error TS2322
  convexLoginUrl: null,
  setConvexLoginUrl: (convexLoginUrl: string) => {
    set({ convexLoginUrl });
  },

  // @ts-expect-error TS2322
  expoQrCodeUrl: null,
  setExpoQrCodeUrl: (expoQrCodeUrl: string) => {
    set({ expoQrCodeUrl });
  },

  loadingStoryboard: null,
  setLoadingStoryboard: (loadingStoryboard: LoadingStoryboard | null) =>
    set({ loadingStoryboard }),

  notifyCodebaseIdsChanged: (codebaseIds: Set<string>) => {
    const supabaseClient = useSupabaseStore.getState().supabaseClient;

    // Find all non-live storyboards that have changed based on the codebase ids in them and set them to load
    const storyboardIdToCodebaseIds = get().storyboardIdToCodebaseIds;
    const storyboardIdToStatus = get().storyboardIdToStatus;

    // For all storyboards, check if they have overlapping codebase ids and set them to loading, clearing the codebase ids
    // Also, clear the storyboard html (it may not be on screen so the Storyboard.tsx file useEffect will not trigger)
    Object.keys(storyboardIdToCodebaseIds).forEach(async (storyboardId) => {
      const storyboardStatus = storyboardIdToStatus[storyboardId];
      if (storyboardStatus?.status === "live") {
        return;
      }

      const storyboardCodebaseIds = storyboardIdToCodebaseIds[storyboardId];

      // Check if this storyboard contains any of the changed codebase IDs
      const hasChangedCodebase = Array.from(codebaseIds).some((codebaseId) =>
        storyboardCodebaseIds.has(codebaseId)
      );

      if (hasChangedCodebase) {
        get().setStoryboardIdToStatus(storyboardId, {
          status: "loading",
          last_updated: new Date(),
        });
        get().setStoryboardIdToCodebaseIds(storyboardId, undefined);

        // Also clear the storyboard html (it may not be on screen so the Storyboard.tsx file useEffect will not trigger)
        if (supabaseClient) {
          const newItem = {
            storyboard_id: storyboardId,
            html: "",
          };
          await supabaseClient
            .from("saved_storyboard_html")
            .update(newItem)
            .eq("storyboard_id", storyboardId);

          useSupabaseStore.getState().onDbSnapshot(
            "savedStoryboardHtml",
            {
              newItems: [newItem],
            },
            undefined,
            "storyboard_id"
          );
        }
      }
    });
  },

  iframeAssignments: {},
  setIframeAssignment: (storyboardId: string, iframeId: string) => {
    const newIframeAssignments = {
      ...get().iframeAssignments,
    };

    // Find any other storyboards that use this iframe and remove the assignment
    Object.keys(newIframeAssignments).forEach((sbId) => {
      if (newIframeAssignments[sbId] === iframeId) {
        delete newIframeAssignments[sbId];
      }
    });

    set((state) => ({
      iframeAssignments: {
        ...newIframeAssignments,
        [storyboardId]: iframeId,
      },
    }));
  },
}));

export const useSelectedNavTreeNode = (): NavTreeNode | null => {
  const selectedElementKey = useSelector(
    (state: any) => state.iframe.selectedElementKey
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup
  );

  // Save the selected nav tree node & multiselected nodes to use inside other components
  return useMemo(() => {
    return elementKeyToNavNodeLookup[selectedElementKey] || null;
  }, [selectedElementKey, elementKeyToNavNodeLookup]);
};

export const useMultiselectedNavTreeNode = (): NavTreeNode[] => {
  const multiSelectedElementKeys = useSelector(
    (state: any) => state.iframe.multiSelectedElementKeys
  );

  const elementKeyToNavNodeLookup = useCanvasStore(
    (state) => state.elementKeyToNavNodeLookup
  );

  // Save the selected nav tree node & multiselected nodes to use inside other components
  return useMemo(() => {
    if (!multiSelectedElementKeys?.length) {
      return [];
    }

    return multiSelectedElementKeys.map(
      (elementKey) => elementKeyToNavNodeLookup[elementKey]
    );
  }, [multiSelectedElementKeys, elementKeyToNavNodeLookup]);
};

export const useCanvasPos = (): CanvasPos => {
  const activeCanvas = useActiveCanvas();

  const canvasIdToPos = useCanvasStore((state) => state.canvasIdToPos);
  return useMemo(() => {
    return activeCanvas?.id
      ? canvasIdToPos?.[activeCanvas?.id] ||
          // canvasIdToPos might not've been set yet if its still debounced
          useCanvasStore.getState().canvasIdToPosRealtime[activeCanvas?.id] ||
          {}
      : {};
  }, [activeCanvas?.id, canvasIdToPos]);
};

// Do not use this unless you need to smoothly react to the value every frame.
export const useCanvasPosRealtime = (): CanvasPos => {
  const activeCanvas = useActiveCanvas();

  const canvasIdToPosRealtime = useCanvasStore(
    (state) => state.canvasIdToPosRealtime
  );

  return useMemo(() => {
    return activeCanvas?.id
      ? canvasIdToPosRealtime?.[activeCanvas?.id] || {}
      : {};
  }, [activeCanvas?.id, canvasIdToPosRealtime]);
};

export const useCanvasZoomRealtime = (): number => {
  const activeCanvas = useActiveCanvas();
  const zoomPerc = useCanvasStore(
    (state) =>
      (activeCanvas?.id &&
        state.canvasIdToPosRealtime[activeCanvas.id]?.zoomPerc) ||
      1
  );

  return zoomPerc;
};

export const isCanvasPosInitialized = (
  canvasPos: CanvasPos
): canvasPos is Required<CanvasPos> => {
  return (
    canvasPos !== undefined &&
    canvasPos.transformX !== undefined &&
    canvasPos.transformY !== undefined &&
    canvasPos.zoomPerc !== undefined
  );
};
